import useStory, { span } from './useStory';
import { Explainer } from './GuideParts';

// GUIDE 10'S ANIMATION (5 Nov 2026): three homes side by side, lit up one
// after another with the VRV that suits each. A flat with its outdoor unit on
// the balcony (VRV Home), a bigger flat with more rooms (VRV S), a large house
// with the outdoor unit on its own roof (VRV Alpha). Under each, a bar shows
// how far the range goes in HP. A drawing, not to scale. Plays once in view,
// rests with all three shown, Replay.

const MAX_BAR = 80; // px for the biggest bar

export default function VrvPick({ watch, sources, homeMaxHp = 6, sMaxHp = 12, alphaMaxHp = 78 }) {
  const story = useStory(18000);
  const t = story.t;
  const a = span(t, 0.05, 0.2);   // the flat
  const b = span(t, 0.28, 0.45);  // the bigger flat
  const c = span(t, 0.55, 0.75);  // the house
  const onA = t < 0.5 || t >= 0.98;
  const onB = (t >= 0.5 && t < 0.75) || t >= 0.98;
  const onC = t >= 0.75;
  // Bars on a log-like scale so 6, 12 and 78 HP all read on a phone.
  const bar = (hp) => Math.round((Math.log(hp + 1) / Math.log(alphaMaxHp + 1)) * MAX_BAR);

  const card = (on, label, hp, k, children) => (
    <div className={`grid gap-1.5 rounded-xl border p-2 text-center ${on ? 'border-blue-600 bg-white' : 'border-gray-200 bg-gray-50'}`}>
      <svg viewBox="0 0 100 90" width="100%" className="block h-auto" aria-hidden="true">{children}</svg>
      <p className="text-[14px] font-extrabold leading-tight text-gray-900">{label}</p>
      <div className="mx-auto h-2 w-full max-w-[90px] rounded bg-gray-100">
        <div className="h-2 rounded bg-blue-600" style={{ width: `${(bar(hp) / MAX_BAR) * 100 * k}%` }} />
      </div>
      <p className="text-[12px] text-gray-600">up to {hp} HP</p>
    </div>
  );

  return (
    <Explainer title={watch.title} captions={watch.captions} story={story} note={watch.note} noteSrc={watch.noteSrc} sources={sources}>
      <div role="img" aria-label="Three homes: a flat with VRV Home on the balcony, a bigger flat with VRV S, and a large house with VRV Alpha on its roof."
        className="grid grid-cols-3 gap-2">
        {card(onA, 'VRV Home', homeMaxHp, a, (
          <g opacity={0.35 + 0.65 * a}>
            <rect x="20" y="20" width="56" height="62" fill="#fff" stroke="#9ca3af" strokeWidth="2" />
            <line x1="20" x2="76" y1="51" y2="51" stroke="#d1d5db" />
            <rect x="76" y="56" width="16" height="3" fill="#9ca3af" />
            <rect x="78" y="44" width="12" height="12" rx="2" fill="#fff" stroke="#2563eb" strokeWidth="1.5" />
            <text x="48" y="14" fontSize="10" fill="#6b7280" textAnchor="middle">Flat</text>
          </g>
        ))}
        {card(onB, 'VRV S', sMaxHp, b, (
          <g opacity={0.35 + 0.65 * b}>
            <rect x="12" y="20" width="66" height="62" fill="#fff" stroke="#9ca3af" strokeWidth="2" />
            <line x1="12" x2="78" y1="51" y2="51" stroke="#d1d5db" />
            <line x1="45" x2="45" y1="20" y2="82" stroke="#d1d5db" />
            <rect x="78" y="56" width="18" height="3" fill="#9ca3af" />
            <rect x="80" y="38" width="14" height="18" rx="2" fill="#fff" stroke="#2563eb" strokeWidth="1.5" />
            <text x="45" y="14" fontSize="10" fill="#6b7280" textAnchor="middle">Bigger flat</text>
          </g>
        ))}
        {card(onC, 'VRV Alpha', alphaMaxHp, c, (
          <g opacity={0.35 + 0.65 * c}>
            <rect x="10" y="34" width="80" height="48" fill="#fff" stroke="#9ca3af" strokeWidth="2" />
            <line x1="10" x2="90" y1="58" y2="58" stroke="#d1d5db" />
            <line x1="37" x2="37" y1="34" y2="82" stroke="#d1d5db" />
            <line x1="63" x2="63" y1="34" y2="82" stroke="#d1d5db" />
            <rect x="34" y="16" width="32" height="18" rx="2" fill="#fff" stroke="#2563eb" strokeWidth="1.5" />
            <circle cx="43" cy="25" r="5" fill="none" stroke="#2563eb" />
            <circle cx="57" cy="25" r="5" fill="none" stroke="#2563eb" />
            <text x="50" y="11" fontSize="10" fill="#6b7280" textAnchor="middle">Large house</text>
          </g>
        ))}
      </div>
    </Explainer>
  );
}
